'use client';

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'src/redux/store';
import { useGetCrmStatusesSuggestionsQuery, useGetObjectDefinitionsListQuery, useGetFieldDefinitionsQuery, useGetMappingsQuery } from 'src/api/redux';
import {
  GenericSelectorModal,
  GenericNodeComponent,
  ConnectorType,
} from '../shared';
import {
  CRMStatusItem,
  crmStatusModalConfig,
  crmStatusNodeConfig,
  transformCRMStatus,
} from './config';

interface CRMStatusComponentProps {
  componentId: string;
  initialCRMStatuses?: CRMStatusItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

const CRMStatusComponent: React.FC<CRMStatusComponentProps> = ({
  componentId,
  initialCRMStatuses = [],
  initialConnector = 'ANY',
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  // Initialize state - ui_metadata provides pre-hydrated objects
  const [crmStatuses, setCrmStatuses] =
    useState<CRMStatusItem[]>(initialCRMStatuses);
  const [connector, setConnector] = useState<ConnectorType>(initialConnector);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Track if this is a new component from a placeholder that hasn't saved yet
  const [isNewFromPlaceholder, setIsNewFromPlaceholder] = useState(
    fromPlaceholder && initialCRMStatuses.length === 0
  );

  // Fetch all CRM statuses upfront (this API doesn't support search/pagination)
  const { data: allCrmStatuses, isLoading } = useGetCrmStatusesSuggestionsQuery(
    { q: '' }
  );

  // Determine if the company uses MedRM by checking enabled mappings
  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  const isMedRM = useMemo(
    () =>
      allMappings?.results?.some(
        (mapping) => mapping.enabled && mapping.crm?.toLowerCase() === 'medrm'
      ) ?? false,
    [allMappings]
  );

  // For MedRM, fetch field definitions to map status values to labels
  const { data: objectDefinitions } = useGetObjectDefinitionsListQuery(
    undefined,
    { skip: !isMedRM }
  );
  const { data: fieldDefinitions } = useGetFieldDefinitionsQuery(
    { objectId: objectDefinitions?.results?.[0]?.id },
    { skip: !isMedRM || !objectDefinitions?.results?.[0]?.id }
  );

  // Create a lookup map from status value to label from MedRM field definitions
  const statusLabelMap = useMemo(() => {
    if (!isMedRM) return new Map<string, string>();
    const statusField = fieldDefinitions?.results?.find(
      (field) => field?.label?.toLowerCase() === 'status'
    );
    if (!statusField?.options) return new Map<string, string>();
    return new Map(
      statusField.options.map((opt) => [opt.value, opt.label])
    );
  }, [isMedRM, fieldDefinitions]);

  // Transform all CRM statuses to standard format
  const allTransformedStatuses: CRMStatusItem[] = useMemo(() => {
    if (!allCrmStatuses) return [];
    return allCrmStatuses.map((status) => transformCRMStatus(status, statusLabelMap));
  }, [allCrmStatuses, statusLabelMap]);

  // Filter results locally based on search term
  const searchResults: CRMStatusItem[] = useMemo(() => {
    if (!searchTerm) return allTransformedStatuses;
    const searchLower = searchTerm.toLowerCase();
    return allTransformedStatuses.filter((status) =>
      status.displayLabel.toLowerCase().includes(searchLower)
    );
  }, [allTransformedStatuses, searchTerm]);

  // Re-map loaded status labels when statusLabelMap becomes available
  useEffect(() => {
    if (statusLabelMap.size === 0 || crmStatuses.length === 0) return;

    const updated = crmStatuses.map((status) => {
      if (status.crm_status && statusLabelMap.has(status.crm_status)) {
        return { ...status, displayLabel: statusLabelMap.get(status.crm_status)! };
      }
      return status;
    });

    const hasChanges = updated.some(
      (s, i) => s.displayLabel !== crmStatuses[i].displayLabel
    );
    if (hasChanges) {
      setCrmStatuses(updated);
    }
  }, [statusLabelMap]);

  // Auto-open modal if starting with no CRM statuses (new component)
  useEffect(() => {
    if (initialCRMStatuses.length === 0 && !isLoading) {
      setShowModal(true);
    }
  }, []); // Only run on mount

  // Sync when external items are added (slash menu merge)
  useEffect(() => {
    if (initialCRMStatuses.length > crmStatuses.length) {
      setCrmStatuses(initialCRMStatuses);
    }
  }, [initialCRMStatuses.length]);

  const handleSave = useCallback(
    (newCrmStatuses: CRMStatusItem[], newConnector: ConnectorType) => {
      if (!newCrmStatuses || newCrmStatuses.length === 0) {
        onRemove(componentId);
        setShowModal(false);
        return;
      }

      setCrmStatuses(newCrmStatuses);
      setConnector(newConnector);

      // Clear the new-from-placeholder flag since we have data now
      setIsNewFromPlaceholder(false);

      onUpdate(componentId, {
        type: 'crm_status',
        value: newCrmStatuses.map((s) => s.id), // Store composite ID: "crm_type-crm_status"
        ui_metadata: newCrmStatuses, // Store full objects for hydration-free loading
        connector: newConnector,
      });

      setShowModal(false);
    },
    [componentId, onUpdate, onRemove]
  );

  // Handle modal close - if from placeholder with no data, remove component
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    // If this was new from a placeholder and user closed without selecting, remove it
    if (isNewFromPlaceholder && crmStatuses.length === 0) {
      onRemove(componentId);
    }
  }, [isNewFromPlaceholder, crmStatuses.length, onRemove, componentId]);

  const handleRemoveItem = useCallback(
    (itemToRemove: CRMStatusItem) => {
      const newCrmStatuses = crmStatuses.filter(
        (s) => s.id !== itemToRemove.id
      );

      if (newCrmStatuses.length === 0) {
        onRemove(componentId);
      } else {
        setCrmStatuses(newCrmStatuses);
        onUpdate(componentId, {
          type: 'crm_status',
          value: newCrmStatuses.map((s) => s.id),
          ui_metadata: newCrmStatuses,
          connector,
        });
      }
    },
    [crmStatuses, connector, componentId, onUpdate, onRemove]
  );

  const handleConnectorChange = useCallback(
    (newConnector: ConnectorType) => {
      setConnector(newConnector);
      onUpdate(componentId, {
        type: 'crm_status',
        value: crmStatuses.map((s) => s.id),
        ui_metadata: crmStatuses,
        connector: newConnector,
      });
    },
    [crmStatuses, componentId, onUpdate]
  );

  // Show loading state while loading data
  if (isLoading && crmStatuses.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 border border-neutral-200 rounded-full text-xs text-neutral-500">
        Loading CRM statuses...
      </span>
    );
  }

  return (
    <>
      {crmStatuses.length > 0 ? (
        <GenericNodeComponent
          config={crmStatusNodeConfig}
          items={crmStatuses}
          connector={connector}
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
          onConnectorChange={handleConnectorChange}
        />
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 bg-teal-50 border border-teal-200 rounded-full cursor-pointer hover:bg-teal-100 transition-colors"
          onClick={() => setShowModal(true)}
        >
          <span className="text-teal-600 text-xs font-medium">
            Select CRM status...
          </span>
        </button>
      )}

      <GenericSelectorModal
        config={crmStatusModalConfig}
        isOpen={showModal}
        selectedItems={crmStatuses}
        connector={connector}
        isEditing={crmStatuses.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </>
  );
};

export default CRMStatusComponent;

// Static method to render into a DOM element (for contentEditable integration)
export const renderCRMStatusComponent = (
  container: HTMLElement,
  props: CRMStatusComponentProps & {
    initialCRMStatuses?: CRMStatusItem[];
    initialConnector?: ConnectorType;
  }
) => {
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <CRMStatusComponent {...props} />
    </Provider>
  );
  return root;
};
